import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addBackendService, updateBackendService} from '../../../actions/infrastructures';
import Dropdown from '../../Dropdown';
import { BASE_URL } from '../../../config/api';

const ServiceModal = ({ isOpen, onClose, serverToEdit, setServerToEdit, projectName }) => {
  const { projectId } = useParams();
  const [serverData, setServerData] = useState({
    gitHubRepo: '',
    branchUsed: '',
    serviceName: '',
    logGroupName: ''
  });
  const [envFile, setEnvFile] = useState(null);
  const fileInputRef = useRef(null);
  const [repositories, setRepositories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchGithubRepos = async () => {
      try {
        const response = await fetch(`https://cloudify-prod.wizr.in/get_org_repos`);
        const data = await response.json();
        setRepositories(data);
      } catch (error) {
        console.error('Error fetching repositories:', error);
      }
    };

    fetchGithubRepos();
  }, []);



  useEffect(() => {
    const fetchBranches = async () => {
      if (serverData.gitHubRepo) {
        try {
          const response = await fetch(`https://cloudify-prod.wizr.in/${serverData.gitHubRepo}/get_branches`);
          const data = await response.json();
          setBranches(data);
        } catch (error) {
          console.error('Error fetching branches:', error);
        }
      }
    };

    fetchBranches();
  }, [serverData.gitHubRepo]);

  useEffect(() => {
    if (serverToEdit) {
      setServerData({
        gitHubRepo: serverToEdit.gitHubRepo,
        branchUsed: serverToEdit.branchUsed,
        serviceName: serverToEdit.serviceName,
        logGroupName: serverToEdit.logGroupName
      });
    }
  }, [serverToEdit]);

  const uploadEnvFile = async (serviceId) => {
    if (!envFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', envFile);
      const serviceName = `${serverData?.gitHubRepo}-${serverData?.branchUsed}`
      const response = await fetch(`https://cloudify-prod.wizr.in/${projectName}/service/${serviceName}/upload/env`, {
        method: 'PUT',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload environment file');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading env file:', error);
      setUploadError('Failed to upload environment file');
      return null;
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadError(null);
    
    try {
      let serviceId;
      if (serverToEdit) {
        serviceId = await dispatch(updateBackendService(projectId, serverToEdit.id, serverData));
      } else {
        serviceId = await dispatch(addBackendService(projectId, serverData));
      }

      // Upload env file if present
      if (envFile && serviceId) {
        await uploadEnvFile(serviceId);
      }

      // Reset form
      setServerData({
        gitHubRepo: '',
        branchUsed: '',
        serviceName: '',
        logGroupName: ''
      });
      setEnvFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    } catch (error) {
      console.error('Error submitting service:', error);
      setUploadError('Failed to create or update service');
    }
  };

  const handleCancel = () => {
    setServerData({
      gitHubRepo: '',
      branchUsed: '',
      serviceName: '',
      logGroupName: ''
    });
    setServerToEdit(null);
    setEnvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };
 
  const handleFileChange = (e) =>{
      const file = e.target.files[0];
      if(file){
        setEnvFile(file);
      }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              {serverToEdit ? 'Edit Service' : 'New Service'}
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{uploadError}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  GitHub Repository
                </label>
                <Dropdown
                  items={repositories}
                  onSelect={(repo) => setServerData({ ...serverData, gitHubRepo: repo })}
                  placeholder="Search GitHub repositories..."
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Branch Used
                </label>
                <Dropdown
                  items={branches}
                  onSelect={(branch) => setServerData({ ...serverData, branchUsed: branch })}
                  placeholder="Search branches..."
                  disabled={!serverData.gitHubRepo}
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Environment File
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".env"
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {serverToEdit ? 'Update Service' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
